import React, { createContext, SetStateAction, useContext, useEffect, useState } from 'react';
import { AlgoliaApiClient } from '../../../../api/localisation/AlgoliaApiClient';
import { getDateDifferenceInDays } from '../../../../utils/date-time';
import { useTracking, trackingContext } from '@jbhi-fi/components/src/Tracking';
import { parseAlgoliaStoreData } from '../utils/parseAlgoliaStoreData';
import { DeliveryLocation, FulfilmentStore, StoreLocation } from '../utils/types';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';

export interface LocalisationContextInterface {
  deliveryLocation: DeliveryLocation;
  selectedStore: StoreLocation;
  isMyLocationOpen?: boolean;
  isLocalisationEnabled: boolean;
  topContainer: 'delivery' | 'pickup';
  shouldCacheAutoRefresh: boolean;
  cacheLifetimeSeconds: number;
  requireFetchManually: boolean;
  excludedProductGroups: string;
  shouldUsePdpHybridPickupSlider: boolean;
  excludedProductFamilyForInStore: string;
  yourStoreFulfilmentOptions?: FulfilmentStore;
  setYourStoreFulfilmentOptions: React.Dispatch<SetStateAction<FulfilmentStore | undefined>>;
}

const MY_DELIVERY_LOCATION_READY = 'myDeliveryLocationReady';
const MY_SELECTED_STORE_READY = 'mySelectedStoreready';
const MY_LOCATION_KEY = 'my-delivery-location';
const MY_SELECTED_STORE_KEY = 'my-selected-store';
const MY_LOCATION_OPEN_TOGGLE = 'my-location-open';

export const LocalisationContext = createContext<LocalisationContextInterface | null>(null);

const storefrontClient = new ShopifyStorefrontClient();

export const LocalisationContextProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [deliveryLocation, setDeliveryLocation] = useState<DeliveryLocation>(getMyDeliveryLocation());
  const [selectedStore, setSelectedStore] = useState<StoreLocation>(getMySelectedStore());
  const [isMyLocationOpen, setIsMyLocationOpen] = useState(false);
  const [yourStoreFulfilmentOptions, setYourStoreFulfilmentOptions] = useState<FulfilmentStore>();

  useEffect(() => {
    const today = new Date();
    const updateStoreData = async () => {
      const store = await AlgoliaApiClient.getStore(selectedStore?.id.toString());

      const storefrontStore = await storefrontClient.getStore(selectedStore?.id, {
        longitude: store._geoloc.lng,
        latitude: store._geoloc.lat,
      });

      const parsedStore = parseAlgoliaStoreData(store);

      parsedStore.shopifyId = storefrontStore?.id.split('/').pop();

      updateSelectedStore(parsedStore);
    };
    if (
      (selectedStore?.lastUpdated && getDateDifferenceInDays(today, new Date(selectedStore.lastUpdated)) >= 2) ||
      (selectedStore && !selectedStore?.shopifyId)
    ) {
      updateStoreData();
      return;
    }
  }, []);

  const handleToggleMyLocation = (event: CustomEvent) => {
    if (event.detail.isOpen !== undefined && event.detail.isOpen !== null) setIsMyLocationOpen(event.detail.isOpen);
  };
  const handleLocationUpdateReady = (event: CustomEvent) => {
    if (event.detail.deliveryLocation) setDeliveryLocation(event.detail.deliveryLocation);
  };
  const handleSelectedStoreUpdateReady = (event: CustomEvent) => {
    if (event.detail.store) setSelectedStore(event.detail.store);
  };

  useEffect(() => {
    window.addEventListener(MY_LOCATION_OPEN_TOGGLE, handleToggleMyLocation);
    window.addEventListener(MY_DELIVERY_LOCATION_READY, handleLocationUpdateReady);
    window.addEventListener(MY_SELECTED_STORE_READY, handleSelectedStoreUpdateReady);
    return () => {
      window.removeEventListener(MY_LOCATION_OPEN_TOGGLE, handleToggleMyLocation);
      window.removeEventListener(MY_DELIVERY_LOCATION_READY, handleLocationUpdateReady);
      window.removeEventListener(MY_SELECTED_STORE_READY, handleSelectedStoreUpdateReady);
    };
  });

  const {
    isLocalisationEnabled,
    topContainer,
    shouldCacheAutoRefresh,
    cacheLifetimeSeconds,
    requireFetchManually,
    excludedProductGroups,
    shouldUsePdpHybridPickupSlider,
    excludedProductFamilyForInStore,
  } = window.Jbhifi?.localisationSettings || {};

  return (
    <LocalisationContext.Provider
      value={{
        isLocalisationEnabled,
        topContainer,
        shouldCacheAutoRefresh,
        cacheLifetimeSeconds,
        requireFetchManually,
        excludedProductGroups,
        shouldUsePdpHybridPickupSlider,
        deliveryLocation,
        selectedStore,
        isMyLocationOpen,
        yourStoreFulfilmentOptions,
        excludedProductFamilyForInStore,
        setYourStoreFulfilmentOptions,
      }}
    >
      {children}
    </LocalisationContext.Provider>
  );
};

export class LocalisationContextHelper {
  static broadcastLocationUpdate = (deliveryLocation: DeliveryLocation) => {
    window.dispatchEvent(new CustomEvent(MY_DELIVERY_LOCATION_READY, { detail: { deliveryLocation } }));
  };

  static broadcastSelectedStoreUpdate = (store: StoreLocation) => {
    window.dispatchEvent(new CustomEvent(MY_SELECTED_STORE_READY, { detail: { store } }));
  };

  static updateIsMyLocationOpen = (isOpen: boolean) => {
    window.dispatchEvent(new CustomEvent(MY_LOCATION_OPEN_TOGGLE, { detail: { isOpen } }));
  };
}

export const useLocalisationContext = () => {
  const context = useContext(LocalisationContext);
  const { tracker } = useTracking({
    context: trackingContext.localisation,
    category: 'localisation',
    group: 'localisation-context',
  });

  if (context === undefined || context === null) {
    throw new Error('useLocalisationContext must be used within a useLocalisationContextProvider');
  }

  const updateMyLocationOpen = (isOpen: boolean, gtmEventSource?: string) => {
    tracker.trackClick({
      label: 'show-localisation',
      source: gtmEventSource,
      extraData: { isOpen },
    });
    LocalisationContextHelper.updateIsMyLocationOpen(isOpen);
  };

  const updateMyDeliveryLocation = (value: DeliveryLocation, gtmEventSource?: string) => {
    setMyDeliveryLocation(value);
    tracker.trackChange({
      label: 'delivery-location-changed',
      source: gtmEventSource,
      extraData: { value: value.postcode },
    });
  };

  const updateMySelectedStore = async (value: StoreLocation, gtmEventSource?: string) => {
    const storefrontStore = await storefrontClient.getStore(value.id, {
      longitude: value.coordinates.longitude,
      latitude: value.coordinates.latitude,
    });

    value.shopifyId = storefrontStore?.id.split('/').pop();

    if (window.Jbhifi.localisationSettings.shouldSaveStoreToCheckout && value.shopifyId) {
      await storefrontClient.setPreferredStore(value.shopifyId);
    }

    updateSelectedStore(value);
    tracker.trackChange({
      label: 'selected-store-changed',
      source: gtmEventSource,
      extraData: { value: value.name },
    });
  };

  const updateMySelectedStoreFromId = async (id: number, gtmEventSource?: string) => {
    const store = await AlgoliaApiClient.getStore(id.toString());

    const parsedStore = parseAlgoliaStoreData(store);

    updateMySelectedStore(parsedStore, gtmEventSource);
  };

  const updateSelectedStoreAndDeliveryLocation = async (location: DeliveryLocation) => {
    const closestStores = await AlgoliaApiClient.getStores({
      query: '',
      approximateLocation: location.coordinates,
      hitsPerPage: 5,
    });
    const filteredStores = closestStores.filter(store => store.isAvailableForClickAndCollect && store.isActive);
    const transformedStore = parseAlgoliaStoreData(filteredStores[0]);

    await updateMySelectedStore(transformedStore);
    updateMyDeliveryLocation(location);
  };

  return {
    ...context,
    updateMyLocationOpen,
    updateMyDeliveryLocation,
    updateMySelectedStore,
    updateMySelectedStoreFromId,
    updateSelectedStoreAndDeliveryLocation,
  };
};

const setMyDeliveryLocation = (location: DeliveryLocation) => {
  window.localStorage.setItem(MY_LOCATION_KEY, JSON.stringify(location));
  LocalisationContextHelper.broadcastLocationUpdate(location);
};

const getMyDeliveryLocation = () => {
  const key = window.localStorage.getItem(MY_LOCATION_KEY);
  return key ? JSON.parse(key) : null;
};

const setMySelectedStore = (store: StoreLocation) => {
  window.localStorage.setItem(MY_SELECTED_STORE_KEY, JSON.stringify(store));
};

const getMySelectedStore = () => {
  const key = window.localStorage.getItem(MY_SELECTED_STORE_KEY);
  return key ? JSON.parse(key) : null;
};
const updateSelectedStore = (value: StoreLocation) => {
  value.lastUpdated = new Date();
  setMySelectedStore(value);
  LocalisationContextHelper.broadcastSelectedStoreUpdate(value);
};
